// Advanced Retirement Planning Types
// Comprehensive types matching RRSP_V4.xlsx input fields

export interface IncomeStage {
  stage: number;
  startingAge: number;
  endingAge: number;
  startingIncome: number;
  endingIncome: number;
  rrspContributions: number;
}

export interface SpendingStage {
  stage: number;
  startingAge: number;
  endingAge: number;
  amount: number;
}

export interface OtherIncomeSource {
  id: string;
  amount: number;
  startAge: number;
  endAge?: number; // Optional: only for ongoing income
  profile: string; // "one time" | "ongoing" (indexed to inflation)
  incomeType: string; // "regular" (100% taxable) | "capital-gains" (50% of amount - costBase taxable) | "non-taxable"
  costBase?: number; // capital-gains only: adjusted cost base of the asset sold
  description: string;
}

export interface CapitalGainsSource {
  id: string;
  amount: number;
  year: number;
  description: string;
}

export interface UserProfile {
  // Personal Information
  age: number;
  gender: string;
  lifeExpectancyScenario: string;
  lifeExpectancy: number;
  residencyYears?: number; // Years of Canadian residency after age 18 (for OAS calculations)
  spouseAge?: number;
  spouseGender?: string;
  spouseLifeExpectancyScenario?: string;
  
  // General Settings
  currentYear: number;
  retirementAge: number;
  numScenarios: number;
  province: string;
  maritalStatus: string; // Changed from optional to required with default
  
  // Income Information - NEW TABLE STRUCTURE
  incomeStages: IncomeStage[];
  spouseIncomeStages?: IncomeStage[];
  
  // Legacy income fields (keep for backward compatibility)
  currentIncome: number;
  expectedRetirementIncome: number;
  spouseCurrentIncome?: number;
  spouseExpectedRetirementIncome?: number;
  
  // Financial Balances
  rrspBalance: number;
  rrifBalance: number;
  liraBalance: number;
  rrspContributionRoom: number;
  tfsaBalance: number;
  tfsaContributionRoom: number;
  tfsaAnnualIncrease: number;
  nonRegisteredBalance: number;
  corporateBalance: number;
  
  // Monthly Contributions (with checkboxes)
  rrspMonthlyContribution: number;
  rrspMonthlyContributionEnabled: boolean;
  tfsaMonthlyContribution: number;
  tfsaMonthlyContributionEnabled: boolean;
  nonRegisteredMonthlyContribution: number;
  nonRegisteredMonthlyContributionEnabled: boolean;
  corporateMonthlyContribution: number;
  corporateMonthlyContributionEnabled: boolean;
  
  // Expected Returns (with checkboxes)
  rrspExpectedReturn: number;
  rrspExpectedReturnEnabled: boolean;
  tfsaExpectedReturn: number;
  tfsaExpectedReturnEnabled: boolean;
  nonRegisteredExpectedReturn: number;
  nonRegisteredExpectedReturnEnabled: boolean;
  corporateExpectedReturn: number;
  corporateExpectedReturnEnabled: boolean;
  
  // Spouse Financial Balances
  spouseRrspBalance?: number;
  spouseRrifBalance?: number;
  spouseLiraBalance?: number;
  spouseRrspContributionRoom?: number;
  spouseTfsaBalance?: number;
  spouseTfsaContributionRoom?: number;
  spouseTfsaAnnualIncrease?: number;
  spouseNonRegisteredBalance?: number;
  spouseCorporateBalance?: number;
  
  // Investment Parameters
  rrspReturnRate: number;
  rrspRiskLevel: number;
  rrspRrifConversionStrategy: string; // RRSP → RRIF conversion strategy (for pension credit & splitting)
  rrifReturnRate: number;
  rrifRiskLevel: number;
  liraReturnRate: number;
  liraRiskLevel: number;
  liraConversionStrategy: string; // LIRA → LIF conversion strategy (like CPP delay)
  liraProvince?: string; // Province where LIRA is registered (determines unlocking rules)
  tfsaReturnRate: number;
  tfsaRiskLevel: number;
  nonRegisteredReturnRate: number;
  nonRegisteredRiskLevel: number;
  corporateReturnRate: number;
  corporateRiskLevel: number;
  
  // Spouse Investment Parameters
  spouseRrspReturnRate?: number;
  spouseRrspRiskLevel?: number;
  spouseRrspRrifConversionStrategy?: string; // Spouse RRSP → RRIF conversion strategy
  spouseRrifReturnRate?: number;
  spouseRrifRiskLevel?: number;
  spouseLiraReturnRate?: number;
  spouseLiraRiskLevel?: number;
  spouseLiraConversionStrategy?: string; // Spouse LIRA → LIF conversion strategy
  spouseLiraProvince?: string; // Province where spouse LIRA is registered
  spouseTfsaReturnRate?: number;
  spouseTfsaRiskLevel?: number;
  spouseNonRegisteredReturnRate?: number;
  spouseNonRegisteredRiskLevel?: number;
  spouseCorporateReturnRate?: number;
  spouseCorporateRiskLevel?: number;
  
  // Income & Employment
  workingProfile: string;
  workingAge: number;
  spouseWorkingProfile?: string;
  spouseWorkingAge?: number;
  spouseRetirementAge?: number;
  
  // Government Benefits
  cppBenefit: number;
  oasBenefit: number;
  oasDelayStrategy: string;
  cppDelayStrategy: string;
  oasDelay: number; // Years to delay OAS (0-5)
  cppDelay: number; // Years to delay CPP (0-5)
  spouseCppBenefit?: number;
  spouseOasBenefit?: number;
  spouseOasDelayStrategy?: string;
  spouseCppDelayStrategy?: string;

  // CPP Survivor Benefits
  cppSurvivorBenefit?: number; // Monthly survivor benefit amount
  spouseCppSurvivorBenefit?: number; // Spouse's survivor benefit

  // Government Benefits Optimization
  cppStartAge?: number;
  oasStartAge?: number;
  optimizeCpp: boolean;
  optimizeOas: boolean;
  
  // Pension Information
  pensionAmount: number;
  pensionStartAge: number;
  pensionInflationRate: number;
  pensionSurvivorBenefitEnabled: boolean;
  pensionSurvivorBenefitPercent: number;
  
  // Spouse Pension (for married couples)
  spousePensionAmount?: number;
  spousePensionStartAge?: number;
  spousePensionInflationRate?: number;
  spousePensionSurvivorBenefitEnabled?: boolean;
  spousePensionSurvivorBenefitPercent?: number;
  
  // Other Income
  otherIncomeAmount: number;
  otherIncomeStartAge: number;
  otherIncomeProfile: string;
  otherIncomeSources?: OtherIncomeSource[];
  
  // Spouse Other Income (for married couples)
  spouseOtherIncomeAmount?: number;
  spouseOtherIncomeStartAge?: number;
  spouseOtherIncomeProfile?: string;
  spouseOtherIncomeSources?: OtherIncomeSource[];
  
  // Spending & Lifestyle - NEW TABLE STRUCTURE
  sharedSpendingStages: SpendingStage[];
  personalSpendingEnabled: boolean;
  personalSpendingStages?: SpendingStage[];
  
  // Legacy spending fields (keep for backward compatibility)
  annualSpending: number;
  spendingScenario?: 'exact' | 'fluctuating' | 'increased';
  spendingPhaseA: number;
  spendingPhaseB: number;
  spendingPhaseC: number;
  
  // Capital Gains Planning
  capitalGainsA: number;
  capitalGainsYearA: number;
  capitalGainsB: number;
  capitalGainsYearB: number;
  capitalGainsC: number;
  capitalGainsYearC: number;
  capitalGainsD: number;
  capitalGainsYearD: number;
  capitalGainsSources?: CapitalGainsSource[];
  
  // Advanced Settings
  inflationRate: number;
  taxProvince: string;
  taxOptimization: boolean;
  
  // Withdrawal Optimization
  withdrawalStrategy?: 'minimize-tax' | 'preserve-estate' | 'manage-risk' | 'grow-net-worth';
  taxImpactAnalysis?: boolean;
  estatePreservationTarget?: number;
  showRiskPercentiles?: boolean;
  
  // Income Splitting (NEW)
  enableIncomeSplitting?: boolean;
  pensionIncomeSplittingPercent?: number; // 0-50%
  splitRRIFIncome?: boolean;
  splitRPPIncome?: boolean;
  
  // Tax Bracket Optimization (NEW)
  targetTaxBracket?: 'low' | 'medium' | 'high';
  avoidClawback?: boolean;
  maxOutTaxBracket?: boolean;
  useTFSAForTaxBracket?: boolean;
  
  // Withholding Tax (NEW)
  includeWithholdingTax?: boolean;
}

export interface ScenarioRequest {
  user_profile: UserProfile;
  num_scenarios: number;
  rrsp_contribution_range: [number, number];
  simulation_years: number;
}

export interface YearlyCashFlow {
  year: number;
  age: number;
  opening_balance: number;
  withdrawals: number;
  contributions: number;
  returns: number;
  closing_balance: number;
  income: number;
  taxes: number;
  net_cash_flow: number;
}

export interface AccountProjection {
  account_type: string;
  yearly_flows: YearlyCashFlow[];
  total_contributions: number;
  total_withdrawals: number;
  total_returns: number;
  final_balance: number;
}

export interface ScenarioResult {
  scenario_id: number;
  rrsp_contribution: number;
  life_expectancy: number;
  spouse_life_expectancy?: number;
  accounts: Record<string, AccountProjection>;
  total_final_balance: number;
  success_rate: number;
}

export interface MonteCarloResponse {
  scenarios?: {
    low: {
      scenario_type: string;
      total_final_balance: number;
      life_expectancy: number;
      success_rate: number;
      rrsp_contribution: number;
      detailed_projection_data: any[];
      chart_data: any;
    };
    average: {
      scenario_type: string;
      total_final_balance: number;
      life_expectancy: number;
      success_rate: number;
      rrsp_contribution: number;
      detailed_projection_data: any[];
      chart_data: any;
    };
    high: {
      scenario_type: string;
      total_final_balance: number;
      life_expectancy: number;
      success_rate: number;
      rrsp_contribution: number;
      detailed_projection_data: any[];
      chart_data: any;
    };
  };
  summary?: {
    total_scenarios: number;
    percentiles: {
      p25_balance: number;
      p50_balance: number;
      p75_balance: number;
    };
    recommendations: string[];
  };
  // Legacy support for old format
  summary_stats?: {
    total_scenarios: number;
    avg_final_balance: number;
    median_final_balance: number;
    min_final_balance: number;
    max_final_balance: number;
    std_final_balance: number;
    avg_success_rate: number;
    scenarios_above_80_percent: number;
    avg_rrsp_contribution: number;
    optimal_rrsp_contribution: number;
  };
  recommendations?: string[];
  risk_analysis?: {
    var_95: number;
    var_90: number;
    worst_case: number;
    best_case: number;
    volatility: number;
  };
  // Withdrawal strategy optimization
  optimizationAnalysis?: {
    strategies: Array<{
      strategy: string;
      afterTaxBalance: number;
    }>;
    optimalStrategy: string;
    optimalAfterTaxBalance: number;
  };
}

// Form section types for better organization
export interface PersonalInfoSection {
  status: 'Single' | 'Married';
  currentYear: number;
  birthYear: number;
  birthMonth: number;
  age: number;
  gender: 'Male' | 'Female';
  lifeExpectancyScenario: string;
  spouseBirthYear?: number;
  spouseBirthMonth?: number;
  spouseAge?: number;
  spouseGender?: 'Male' | 'Female';
  spouseLifeExpectancyScenario?: string;
}

export interface FinancialBalancesSection {
  rrspBalance: number;
  tfsaBalance: number;
  tfsaContributionRoom: number;
  tfsaAnnualIncrease: number;
  nonRegisteredBalance: number;
}

export interface InvestmentSettingsSection {
  rrspReturnRate: number;
  rrspRiskLevel: number;
  tfsaReturnRate: number;
  tfsaRiskLevel: number;
  nonRegisteredReturnRate: number;
  nonRegisteredRiskLevel: number;
}

export interface IncomeEmploymentSection {
  currentIncome: number;
  workingProfile: string;
  workingAge: number;
  retirementAge: number;
  spouseWorkingProfile?: string;
  spouseWorkingAge?: number;
  spouseRetirementAge?: number;
}

export interface GovernmentBenefitsSection {
  cppBenefit: number;
  oasBenefit: number;
  oasDelayStrategy: string;
  cppDelayStrategy: string;
}

export interface PensionSection {
  pensionAmount: number;
  pensionStartAge: number;
  pensionInflationRate: number;
  pensionSurvivorBenefit: number;
  spousePensionAmount?: number;
  spousePensionStartAge?: number;
  spousePensionInflationRate?: number;
  spousePensionSurvivorBenefit?: number;
}

export interface OtherIncomeSection {
  otherIncomeAmount: number;
  otherIncomeStartAge: number;
  otherIncomeProfile: string;
  spouseOtherIncomeAmount?: number;
  spouseOtherIncomeStartAge?: number;
  spouseOtherIncomeProfile?: string;
}

export interface SpendingLifestyleSection {
  annualSpending: number;
  spendingPhaseA: number;
  spendingPhaseB: number;
  spendingPhaseC: number;
}

export interface CapitalGainsSection {
  capitalGainsA: number;
  capitalGainsYearA: number;
  capitalGainsB: number;
  capitalGainsYearB: number;
  capitalGainsC: number;
  capitalGainsYearC: number;
  capitalGainsD: number;
  capitalGainsYearD: number;
}

export interface AdvancedSettingsSection {
  inflationRate: number;
  taxProvince: string;
  taxOptimization: boolean;
}

// Predefined options
export const LIFE_EXPECTANCY_SCENARIOS = [
  'General',
  'Pessimistic (shorter life)',
  'Optimistic (longer life)',
  'Defined - Live to 50',
  'Defined - Live to 51',
  'Defined - Live to 52',
  'Defined - Live to 53',
  'Defined - Live to 54',
  'Defined - Live to 55',
  'Defined - Live to 56',
  'Defined - Live to 57',
  'Defined - Live to 58',
  'Defined - Live to 59',
  'Defined - Live to 60',
  'Defined - Live to 61',
  'Defined - Live to 62',
  'Defined - Live to 63',
  'Defined - Live to 64',
  'Defined - Live to 65',
  'Defined - Live to 66',
  'Defined - Live to 67',
  'Defined - Live to 68',
  'Defined - Live to 69',
  'Defined - Live to 70',
  'Defined - Live to 71',
  'Defined - Live to 72',
  'Defined - Live to 73',
  'Defined - Live to 74',
  'Defined - Live to 75',
  'Defined - Live to 100',
  'Defined - Live to 107'
];

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const PROVINCES = [
  'Ontario', 'Quebec', 'British Columbia', 'Alberta', 'Manitoba',
  'Saskatchewan', 'Nova Scotia', 'New Brunswick', 'Newfoundland and Labrador',
  'Prince Edward Island', 'Northwest Territories', 'Nunavut', 'Yukon'
];

export const OAS_DELAY_STRATEGIES = [
  'Start early at 60',
  'Start early at 61',
  'Start early at 62',
  'Start early at 63',
  'Start early at 64',
  'Start at 65',
  'Delay until 66',
  'Delay until 67',
  'Delay until 68',
  'Delay until 69',
  'Delay until 70',
  'Calculate Suggested Strategy'
];

export const CPP_DELAY_STRATEGIES = [
  'Start early at 60',
  'Start early at 61',
  'Start early at 62',
  'Start early at 63',
  'Start early at 64',
  'Start at 65',
  'Delay until 66',
  'Delay until 67',
  'Delay until 68',
  'Delay until 69',
  'Delay until 70',
  'Calculate Suggested Strategy'
];

export const LIRA_CONVERSION_STRATEGIES = [
  'Convert at 55',
  'Convert at 56',
  'Convert at 57',
  'Convert at 58',
  'Convert at 59',
  'Convert at 60',
  'Convert at 61',
  'Convert at 62',
  'Convert at 63',
  'Convert at 64',
  'Convert at 65',
  'Convert at 66',
  'Convert at 67',
  'Convert at 68',
  'Convert at 69',
  'Convert at 70',
  'Convert at 71',
  'Calculate Optimal Conversion Age'
];

export const LIRA_PROVINCES = [
  { value: 'AB', label: 'Alberta', unlocking: '50%' },
  { value: 'BC', label: 'British Columbia', unlocking: '0%' },
  { value: 'MB', label: 'Manitoba', unlocking: '100%' },
  { value: 'NB', label: 'New Brunswick', unlocking: '0%' },
  { value: 'NL', label: 'Newfoundland', unlocking: '0%' },
  { value: 'NS', label: 'Nova Scotia', unlocking: '0%' },
  { value: 'ON', label: 'Ontario', unlocking: '50%' },
  { value: 'PE', label: 'Prince Edward Island', unlocking: '50%' },
  { value: 'QC', label: 'Quebec', unlocking: '0%' },
  { value: 'SK', label: 'Saskatchewan', unlocking: '100%' },
  { value: 'FEDERAL', label: 'Federal', unlocking: '50%' }
];

export const RRSP_RRIF_CONVERSION_STRATEGIES = [
  'Convert at 55',
  'Convert at 56',
  'Convert at 57',
  'Convert at 58',
  'Convert at 59',
  'Convert at 60',
  'Convert at 61',
  'Convert at 62',
  'Convert at 63',
  'Convert at 64',
  'Convert at 65',
  'Convert at 66',
  'Convert at 67',
  'Convert at 68',
  'Convert at 69',
  'Convert at 70',
  'Convert at 71',
  'Calculate Optimal Conversion Age'
];

export const WORKING_PROFILES = [
  'Steady Income',
  'Increasing Income',
  'Decreasing Income',
  'Variable Income'
];

export const INCOME_PROFILES = [
  'Steady',
  'Increasing',
  'Decreasing',
  'Variable'
]; 